<!-- SettingsScreen.vue - Figma "Einstellungen · Varianten", variant 1 (grouped list).
     User settings first, build info as one quiet line, legal pages live in the Mehr sheet. -->
<template>
  <div class="settings">
    <div class="settings-top">
      <button type="button" class="back" :aria-label="$t('account.settings.back')" @click="goBack">
        <i class="fas fa-chevron-left" aria-hidden="true"></i>
      </button>
      <h1>{{ $t('account.settings.title') }}</h1>
    </div>

    <!-- Account -->
    <section class="group" :aria-label="$t('account.settings.account')">
      <div v-if="user" class="account">
        <span class="avatar" aria-hidden="true">{{ initial }}</span>
        <span class="account-txt">
          <strong>{{ user.username || user.name }}</strong>
          <span v-if="user.email" class="muted">{{ user.email }}</span>
        </span>
      </div>
      <router-link v-else :to="$lp('/profile')" class="row">
        <span class="ic" aria-hidden="true"><i class="fas fa-user"></i></span>
        <span class="row-txt">
          <span class="label">{{ $t('account.settings.login') }}</span>
          <span class="hint">{{ $t('account.settings.guestHint') }}</span>
        </span>
        <i class="fas fa-chevron-right chev" aria-hidden="true"></i>
      </router-link>
    </section>

    <!-- Appearance -->
    <h2 class="group-title" id="set-appearance">{{ $t('account.settings.appearance') }}</h2>
    <section class="group" aria-labelledby="set-appearance">
      <div class="row static">
        <span class="ic" aria-hidden="true"><i class="fas fa-moon"></i></span>
        <span class="label" id="set-design">{{ $t('account.settings.design') }}</span>
      </div>
      <div class="seg" role="radiogroup" aria-labelledby="set-design">
        <button v-for="t in themes" :key="t" type="button" role="radio" :aria-checked="theme === t ? 'true' : 'false'"
          :class="{ on: theme === t }" @click="setTheme(t)">
          {{ $t(themeLabels[t]) }}
        </button>
      </div>
    </section>

    <!-- Language & info -->
    <h2 class="group-title" id="set-lang">{{ $t('account.settings.languageInfo') }}</h2>
    <section class="group" aria-labelledby="set-lang">
      <div class="row static">
        <span class="ic" aria-hidden="true"><i class="fas fa-globe"></i></span>
        <span class="label">{{ $t('common.language.label') }}</span>
        <LanguageSwitcher class="push" />
      </div>
      <hr />
      <router-link :to="$lp('/about')" class="row">
        <span class="ic" aria-hidden="true"><i class="fas fa-circle-info"></i></span>
        <span class="label">{{ $t('account.settings.about') }}</span>
        <i class="fas fa-chevron-right chev" aria-hidden="true"></i>
      </router-link>
    </section>

    <section v-if="user" class="group">
      <button type="button" class="row danger" @click="onLogoutClick">
        <span class="ic" aria-hidden="true"><i class="fas fa-right-from-bracket"></i></span>
        <span class="label">{{ $t('account.settings.logout') }}</span>
      </button>
    </section>

    <p class="build" :aria-label="$t('account.settings.buildInfo')">
      Version {{ release }} · Build {{ buildDate }}<template v-if="commit"> ·
        <a :href="repo + '/commit/' + commit" target="_blank" rel="noopener noreferrer">{{ commit }}</a></template>
    </p>
  </div>
</template>

<script>
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import { mapActions } from 'vuex';

export default {
  name: 'SettingsScreen',
  components: { LanguageSwitcher },
  data() {
    return {
      themes: ['light', 'dark', 'system'],
      themeLabels: { light: 'account.settings.themeLight', dark: 'account.settings.themeDark', system: 'account.settings.themeSystem' },
      release: process.env.VUE_APP_RELEASE,
      commit: process.env.VUE_APP_COMMIT,
      repo: 'https://github.com/philffm/smoothride_ddd_philjwornath',
      user: (this.$pb && this.$pb.authStore && this.$pb.authStore.model) || null,
    };
  },
  computed: {
    theme() { return this.$store.state.theme; },
    initial() { const n = (this.user && (this.user.username || this.user.name || this.user.email)) || '?'; return n.charAt(0).toUpperCase(); },
    buildDate() {
      const d = new Date(process.env.VUE_APP_BUILD_TIME);
      return isNaN(d) ? '' : d.toLocaleDateString(this.$i18n.locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
  },
  methods: {
    ...mapActions(['logout']),
    setTheme(t) { this.$store.commit('setTheme', t); },
    goBack() { if (window.history.length > 1) this.$router.back(); else this.$router.push(this.$lp('/')); },
    onLogoutClick() {
      this.logout({ $pb: this.$pb });
      this.user = null;
      this.$router.push(this.$lp('/'));
    },
  },
};
</script>

<style scoped>
.settings { width: 100%; max-width: 480px; margin: 0 auto; display: flex; flex-direction: column; gap: 12px; text-align: left; }
.settings-top { display: flex; flex-direction: column; align-items: flex-start; gap: 12px; margin-bottom: 4px; }
.settings-top h1 { font-size: 24px; font-weight: 600; margin: 0; }
.back { width: 40px; height: 40px; border-radius: 50%; border: 0; background: var(--color-band, var(--color-subtle)); color: var(--color-text); cursor: pointer; display: grid; place-items: center; }
.back:hover { background: var(--color-subtle); }
.group-title { text-align: left; font-size: 11px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--color-secondary); margin: 12px 8px 0; }
.group { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 22px; padding: 6px 16px; }
.group hr { border: 0; border-top: 1px solid var(--color-border); margin: 4px 0; }
.row { display: flex; align-items: center; gap: 14px; width: 100%; min-height: 52px; padding: 6px 0; box-sizing: border-box;
  background: none; border: 0; font: inherit; text-align: left; text-decoration: none; color: var(--color-text) !important; cursor: pointer; }
.row.static { cursor: default; }
a.row:hover .label, button.row:hover .label { text-decoration: underline; }
.ic { flex: none; width: 36px; height: 36px; border-radius: 12px; display: grid; place-items: center; background: var(--color-band, var(--color-subtle)); font-size: 15px; }
.label { font-size: 15px; font-weight: 500; }
.row-txt { flex: 1; display: flex; flex-direction: column; }
.row > .label { flex: 1; }
.hint, .muted { font-size: 12px; color: var(--color-secondary); }
.chev { font-size: 12px; color: var(--color-secondary); }
.push { margin-left: auto; }
.danger, .danger .label { color: #c0392b !important; }
.dark-mode .danger, .dark-mode .danger .label { color: #ff8a7a !important; }
.account { display: flex; align-items: center; gap: 14px; padding: 10px 0; }
.avatar { width: 44px; height: 44px; border-radius: 50%; background: var(--color-primary); color: var(--color-on-primary); display: grid; place-items: center; font-weight: 600; font-size: 18px; }
.account-txt { display: flex; flex-direction: column; min-width: 0; }
.account-txt strong { font-size: 15px; font-weight: 600; }
.seg { display: flex; gap: 2px; padding: 4px; margin: 4px 0 10px; border-radius: 14px; background: var(--color-band, var(--color-subtle)); }
.seg button { flex: 1; min-height: 36px; border: 0; border-radius: 11px; background: transparent; color: var(--color-secondary); font: inherit; font-size: 13px; cursor: pointer; }
.seg button.on { background: var(--color-primary); color: var(--color-on-primary); font-weight: 600; }
.seg button:focus-visible, .back:focus-visible, .row:focus-visible { outline: 2px solid var(--color-focus, var(--color-primary)); outline-offset: 2px; }
.build { font-family: var(--font-mono); font-size: 11px; color: var(--color-secondary); text-align: center; margin: 8px 0 0; }
.build a { color: inherit !important; }
</style>
